/**
 * /academy/home for the TEF Canada track (D-109): the goal (NCLC, date, centre), the one next
 * step (free placement, resume, next lesson, mock exam), the latest estimated NCLC of each
 * ability (score out of 699), the official structure of the exam read from the format table, the
 * course shared with the TCF plus the TEF strategies, « Méthode et astuces TEF » and the urgent
 * session mocks. Every level shown is an estimate, never an official result.
 */

import {
  ApiError,
  type Learner,
  getAcademyAttemptsListQueryKey,
  getAcademyMeRetrieveQueryKey,
  getAcademyUrgentListQueryKey,
  useAcademyAttemptsCreate,
  useAcademyAttemptsList,
  useAcademyMeExamsCreate,
  useAcademyMeRetrieve,
  useAcademyProgressRetrieve,
} from "@agencyos/api-client";
import { type ExamProfile, formatScore, parseProfiles, sittingStep } from "@agencyos/core";
import { Badge, Button, GlassCard, SelectField, Skeleton, TextField, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ClipboardList, Compass, MapPin, PencilLine, Play, Route, Target } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { fieldErrors } from "../../lib/settings";
import { MethodHomeEntry } from "../ielts/CourseEntries";
import { useCoursePath, useIeltsFormat } from "../ielts/CoursePage";
import { TEF } from "../ielts/exam";
import { ExamStructure } from "../MocksPage";
import { dateText, daysUntil } from "../shared";
import { SKILLS, isObject } from "../types";
import { CardTitle, PageHead, SectionTitle, SkillIcon } from "../ui";
import { HomeExtras } from "../coaching/HomeExtras";
import { RenewalBanner } from "../sales/RenewalBanner";
import { UrgentMocks } from "../UrgentMocks";
import { ExamDatesCard } from "../ExamDatesCard";
import "./tef.css";
import { MotherTonguePrompt } from "../ielts/MotherTongue";

const NCLC_TARGETS = [4, 5, 6, 7, 8, 9, 10, 11, 12];

function useTefProfile(learner: Learner | undefined): ExamProfile | null {
  return useMemo(() => parseProfiles(learner?.exam_profiles).find((p) => p.exam === TEF) ?? null, [learner]);
}

/** Goal of the TEF track (NCLC, date, centre), saved with POST academy/me/exams. */
export function TefGoalForm({ profile, onDone, first = false }: { profile: ExamProfile | null; onDone?: () => void; first?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = useAcademyMeExamsCreate();
  const format = useIeltsFormat(TEF);
  const [target, setTarget] = useState(String(profile?.target ?? 7));
  const [examDate, setExamDate] = useState(profile?.examDate ?? "");
  const [center, setCenter] = useState(profile?.examCenter ?? "");
  const errors = fieldErrors(save.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      { data: { exam: TEF, target: Number(target), exam_date: examDate || null, exam_center: center } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
          void queryClient.invalidateQueries({ queryKey: getAcademyUrgentListQueryKey({ exam: TEF }) });
          onDone?.();
        },
      },
    );
  };
  return (
    <GlassCard as="section" className="ac-card ac-card--accent" aria-labelledby="tef-goals">
      <form className="ac-form" onSubmit={onSubmit}>
        <CardTitle id="tef-goals" icon={<Target size={16} />} tone="warning">
          {first ? t("tef.goal.followTitle") : t("tef.goal.title")}
        </CardTitle>
        <p className="ac-secondary">{t("tef.goal.hint")}</p>
        <div className="ac-form-grid">
          <SelectField
            label={t("tef.goal.target")}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            options={NCLC_TARGETS.map((n) => ({ value: String(n), label: t("tef.nclc", { n }) }))}
            error={errors.target}
          />
          <TextField label={t("academy.goals.examDate")} type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} error={errors.exam_date} />
          <SelectField
            label={t("tef.goal.center")}
            hint={t("tef.goal.centerHint")}
            value={center}
            onChange={(e) => setCenter(e.target.value)}
            options={[
              { value: "", label: t("academy.goals.choose") },
              ...format.centers.map((c) => ({ value: c.code, label: `${c.city} · ${c.name}${c.confirmed ? "" : ` (${t("tef.goal.toConfirm")})`}` })),
            ]}
            error={errors.exam_center}
          />
        </div>
        {save.isError && !Object.keys(errors).length && <p className="ko">{t("common.error")}</p>}
        <div className="ac-card__actions">
          {onDone && !first && (
            <Button variant="ghost" onClick={onDone}>
              {t("common.cancel")}
            </Button>
          )}
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t("common.loading") : first ? t("tef.goal.follow") : t("academy.goals.save")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

interface SkillLevel {
  score: number | null;
  nclc: number | null;
}

function skillLevels(value: unknown): Record<string, SkillLevel> {
  const analysis = isObject(value) && isObject(value.analysis) ? value.analysis : {};
  const skills = isObject(analysis.skills) ? analysis.skills : {};
  const out: Record<string, SkillLevel> = {};
  for (const skill of SKILLS) {
    const row = isObject(skills[skill]) ? skills[skill] : {};
    out[skill] = {
      score: typeof row.score === "number" ? row.score : null,
      nclc: typeof row.nclc === "number" ? row.nclc : null,
    };
  }
  return out;
}

export function TefHome() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useAcademyMeRetrieve({ query: { retry: false } });
  const learner = me.data?.status === 200 ? me.data.data : undefined;
  const profile = useTefProfile(learner);
  const hasAccess = learner?.access?.[TEF] === true;
  const [editing, setEditing] = useState(false);
  const progress = useAcademyProgressRetrieve({ exam: TEF }, { query: { retry: false, enabled: Boolean(learner) } });
  const attempts = useAcademyAttemptsList({ exam: TEF }, { query: { retry: false, enabled: Boolean(learner) } });
  const { path } = useCoursePath(Boolean(learner) && hasAccess, TEF);
  const create = useAcademyAttemptsCreate();
  const levels = useMemo(() => skillLevels(progress.data?.status === 200 ? progress.data.data : null), [progress.data]);

  if (me.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (!learner) return <p className="ko">{t("academy.player.networkError")}</p>;

  const list = attempts.data?.status === 200 ? attempts.data.data : [];
  const placed = Boolean(profile?.placed);
  // D-160: a paused mock exam never hides (nor stands for) the placement test
  const sitting = sittingStep(list, placed);
  const running = sitting?.step === "resume" ? sitting.open : undefined;
  const placementOpen = sitting?.step === "placement" ? sitting.open : undefined;
  const otherOpen = list.find((a) => a.status === "in_progress" && a.kind !== "placement");
  const startPlacement = () =>
    create.mutate(
      { data: { exam: TEF, kind: "placement", skills: ["co", "ce"], mode: "exam" } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey({ exam: TEF }) });
          navigate(`/academy/attempt/${response.data.id}`);
        },
      },
    );
  const days = daysUntil(profile?.examDate ?? null);
  const startError = create.error instanceof ApiError ? (create.error.detail ?? t("common.error")) : create.error ? t("common.error") : "";

  return (
    <div className="ac-page ac-tef">
      <PageHead eyebrow={t("ielts.exams.tef_canada.name")} title={t("tef.home.title")} subtitle={<p className="ac-note" role="note">{t("tef.estimateLong")}</p>} />

      {!profile || editing ? (
        <TefGoalForm profile={profile} first={!profile} onDone={profile ? () => setEditing(false) : undefined} />
      ) : (
        <GlassCard as="section" className="ac-card ac-goal" aria-labelledby="tef-goal-title">
          <div className="ac-card__head">
            <CardTitle id="tef-goal-title" icon={<Target size={16} />} tone="warning">
              {t("academy.ui.home.goalTitle")}
            </CardTitle>
            <Button variant="ghost" size="sm" icon={<PencilLine aria-hidden size={15} />} onClick={() => setEditing(true)}>
              {t("academy.goals.edit")}
            </Button>
          </div>
          <div className="ac-goal__figures">
            <div className="ac-goal__figure">
              <span className="ac-goal__label">{t("academy.progress.target")}</span>
              <span className="ac-goal__value ao-num">{t("tef.nclc", { n: profile.target ?? 7 })}</span>
            </div>
            <div className="ac-goal__figure">
              <span className="ac-goal__label">{t("academy.goals.examDate")}</span>
              <span className="ac-goal__value">{profile.examDate ? dateText(profile.examDate, i18n.language) : "—"}</span>
              {days !== null && days >= 0 && <span className="ac-caption">{t("tef.home.daysLeft", { count: days })}</span>}
            </div>
            <div className="ac-goal__figure">
              <span className="ac-goal__label">
                <MapPin aria-hidden size={13} /> {t("tef.goal.center")}
              </span>
              <span className="ac-goal__value">{profile.examCenter || "—"}</span>
            </div>
          </div>
        </GlassCard>
      )}

      <GlassCard as="section" className="ac-card ac-next" aria-labelledby="tef-next">
        <CardTitle id="tef-next" icon={<Compass size={16} />} tone="info">
          {t("tef.home.next")}
        </CardTitle>
        {running ? (
          <div className="ac-card__actions ac-card__actions--start">
            <p className="ac-secondary">{t("tef.home.resumeText")}</p>
            <Link className={buttonClass("primary")} to={`/academy/attempt/${running.id}`}>
              <Play aria-hidden size={16} /> {t("academy.history.resume")}
            </Link>
          </div>
        ) : sitting?.step === "placement" ? (
          <div className="ac-card__actions ac-card__actions--start">
            <p className="ac-secondary">{placementOpen ? t("academy.placement.inProgress") : t("tef.home.placementText")}</p>
            {placementOpen ? (
              <Link className={buttonClass("primary")} to={`/academy/attempt/${placementOpen.id}`}>
                <Play aria-hidden size={16} /> {t("academy.placement.resume")}
              </Link>
            ) : (
              <Button icon={<Play aria-hidden size={16} />} disabled={create.isPending} onClick={startPlacement}>
                {create.isPending ? t("common.loading") : t("tef.home.placement")}
              </Button>
            )}
            {otherOpen && (
              <Link className={buttonClass("ghost")} to={`/academy/attempt/${otherOpen.id}`}>
                {t("academy.ui.home.resumeTitle", { kind: t(`academy.kind.${otherOpen.kind}`) })}
              </Link>
            )}
            {startError && <p className="ko">{startError}</p>}
          </div>
        ) : (
          <div className="ac-card__actions ac-card__actions--start">
            <p className="ac-secondary">{hasAccess ? t("tef.home.courseText", { done: path?.completed ?? 0, total: path?.total ?? 0 }) : t("tef.home.needPlan")}</p>
            {hasAccess ? (
              <>
                <Link className={buttonClass("primary")} to="/academy/course">
                  <Route aria-hidden size={16} /> {t("tef.home.course")}
                </Link>
                <Link className={buttonClass("secondary")} to="/academy/mocks">
                  <ClipboardList aria-hidden size={16} /> {t("tef.home.mocks")}
                </Link>
              </>
            ) : (
              <Link className={buttonClass("primary")} to="/academy/plans">
                {t("tef.home.plans")} <ArrowRight aria-hidden size={16} />
              </Link>
            )}
          </div>
        )}
      </GlassCard>

      <RenewalBanner />
      <HomeExtras />
      <MotherTonguePrompt />
      <UrgentMocks locked={!hasAccess} />
      <ExamDatesCard exam="tef_canada" />

      <section className="ac-section" aria-labelledby="tef-levels">
        <SectionTitle id="tef-levels">{t("tef.home.levels")}</SectionTitle>
        <ul className="ac-tef-levels">
          {SKILLS.map((skill) => {
            const row = levels[skill];
            return (
              <li key={skill} className={`ac-tef-level ac-surface ac-skill--${skill}`}>
                <SkillIcon skill={skill} size={16} />
                <span className="ac-tef-level__name">{t(`tef.skill.${skill}`)}</span>
                <span className="ac-tef-level__value ao-num">{row?.nclc ? t("tef.nclc", { n: row.nclc }) : "—"}</span>
                <span className="ac-caption ao-num">{formatScore(row?.score ?? null)}</span>
              </li>
            );
          })}
        </ul>
        <p className="ac-footnote">{t("tef.home.levelsNote")}</p>
      </section>

      <section className="ac-section" aria-labelledby="tef-structure">
        <SectionTitle id="tef-structure">{t("tef.home.structure")}</SectionTitle>
        <ExamStructure />
        <p className="ac-footnote">
          <Badge>{t("tef.home.organizer")}</Badge> {t("tef.home.structureNote")}
        </p>
      </section>

      <MethodHomeEntry exam={TEF} />
    </div>
  );
}
